// @ts-check
/** @typedef {{void:string,haze:string,tower:string,floor:string,wire:string,cyan:string,violet:string,light:string,metal:string,steel:string}} Palette */

/** @param {number} seed @param {number} salt */
export function variation(seed, salt) {
  let value = Math.imul(seed ^ salt, 0x45d9f3b);
  value = Math.imul(value ^ (value >>> 16), 0x45d9f3b);
  return ((value ^ (value >>> 16)) >>> 0) / 4294967296;
}

/** @param {CanvasRenderingContext2D} ctx @param {number[][]} points */
export function trace(ctx, points) {
  ctx.beginPath();
  points.forEach(([x, y], index) => index ? ctx.lineTo(x, y) : ctx.moveTo(x, y));
  ctx.stroke();
}

/** Manufactured housing with a visible top bevel, recessed face, and fasteners.
 * @param {CanvasRenderingContext2D} ctx @param {number} x @param {number} y
 * @param {number} w @param {number} h @param {Palette} c @param {number} [bevel]
 */
function housing(ctx, x, y, w, h, c, bevel = 8) {
  const metal = ctx.createLinearGradient(x, y, x + w, y + h);
  metal.addColorStop(0, c.steel); metal.addColorStop(0.08, c.metal);
  metal.addColorStop(0.6, c.tower); metal.addColorStop(1, c.metal);
  ctx.fillStyle = metal;
  ctx.beginPath(); ctx.moveTo(x + bevel, y); ctx.lineTo(x + w - bevel, y);
  ctx.lineTo(x + w, y + bevel); ctx.lineTo(x + w, y + h - bevel);
  ctx.lineTo(x + w - bevel, y + h); ctx.lineTo(x + bevel, y + h);
  ctx.lineTo(x, y + h - bevel); ctx.lineTo(x, y + bevel); ctx.closePath(); ctx.fill();
  ctx.strokeStyle = c.wire; ctx.lineWidth = 1; ctx.stroke();
  ctx.fillStyle = c.floor; ctx.fillRect(x + bevel, y + bevel, w - bevel * 2, h - bevel * 2);
  ctx.strokeStyle = c.steel; ctx.globalAlpha *= 0.65;
  trace(ctx, [[x + bevel, y + 1], [x + w - bevel, y + 1], [x + w - 1, y + bevel]]);
  ctx.globalAlpha /= 0.65;
  ctx.fillStyle = c.steel;
  for (const dx of [5, w - 7]) for (const dy of [5, h - 7]) ctx.fillRect(x + dx, y + dy, 2, 2);
}

/** Backplane: an address lattice and buried buses, with no astronomical scenery.
 * @param {CanvasRenderingContext2D} ctx @param {number} width @param {number} height @param {Palette} c
 */
export function paintAtmosphere(ctx, width, height, c) {
  const wash = ctx.createLinearGradient(0, 0, 0, height);
  wash.addColorStop(0, c.void); wash.addColorStop(0.55, c.haze); wash.addColorStop(1, c.void);
  ctx.fillStyle = wash; ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = c.wire; ctx.lineWidth = 1; ctx.globalAlpha = 0.11;
  for (let x = 24; x < width; x += 96) trace(ctx, [[x, 0], [x, height]]);
  for (let y = 32; y < height; y += 96) trace(ctx, [[0, y], [width, y]]);
  ctx.fillStyle = c.steel; ctx.globalAlpha = 0.18;
  for (let x = 24; x < width; x += 96) for (let y = 32; y < height; y += 96) ctx.fillRect(x - 1, y - 1, 3, 3);
  ctx.globalAlpha = 1;
}

/** Reusable, cacheable machinery tile. Layer zero contains monumental sockets;
 * layer one contains the nearer boards that establish depth and material.
 * @param {CanvasRenderingContext2D} ctx @param {number} index @param {number} width
 * @param {number} height @param {number} layer @param {Palette} c
 */
export function paintMachinery(ctx, index, width, height, layer, c) {
  ctx.clearRect(0, 0, width, height);
  if (layer === 0) {
    const size = Math.min(height * 0.67, 340), x = width * 0.48 - size / 2, y = height * 0.12;
    ctx.globalAlpha = 0.5; ctx.strokeStyle = c.wire; ctx.lineWidth = 2;
    for (let i = 0; i < 10; i++) {
      const sy = y + 28 + i * (size - 56) / 9;
      const bend = 36 + i * 12;
      trace(ctx, [[0, sy + bend], [x - bend, sy + bend], [x - 20, sy], [x, sy]]);
      trace(ctx, [[x + size, sy], [x + size + 20, sy], [x + size + bend, sy - bend], [width - 72, sy - bend], [width - 24, sy + bend], [width, sy + bend]]);
    }
    ctx.globalAlpha = 0.82;
    housing(ctx, x, y, size, size, c, 18);
    housing(ctx, x + 28, y + 28, size - 56, size - 56, c, 6);
    ctx.fillStyle = c.void; ctx.fillRect(x + 41, y + 41, size - 82, size - 82);
    for (let row = 0; row < 13; row++) for (let col = 0; col < 13; col++) {
      ctx.globalAlpha = 0.12 + variation(row * 13 + col, index + 19) * 0.3;
      ctx.fillStyle = (row + col) % 7 ? c.steel : c.cyan;
      ctx.fillRect(x + 48 + col * (size - 100) / 13, y + 48 + row * (size - 100) / 13, 3, 3);
    }
    ctx.globalAlpha = 0.7; ctx.fillStyle = c.cyan;
    for (let i = 0; i < 8; i++) {
      ctx.fillRect(x + 34 + i * (size - 68) / 8, y + 9, 9, 2);
      ctx.fillRect(x + 34 + i * (size - 68) / 8, y + size - 11, 9, 2);
    }
    ctx.font = '9px monospace'; ctx.fillStyle = c.steel;
    ctx.fillText(`BOOT / ${String((index % 99 + 99) % 99).padStart(2, '0')}`, x + 12, y + size + 24);
    ctx.globalAlpha = 0.22; ctx.strokeStyle = c.cyan;
    for (let i = 0; i < 6; i++) trace(ctx, [[x + 40 + i * 14, y + size + 36], [x + 40 + i * 14, height - 30], [width, height - 30 - i * 6]]);
  } else {
    for (let i = 0; i < 5; i++) {
      const x = i * width / 5 + 18, w = width / 5 - 42;
      const h = height * (0.22 + variation(index * 5 + i, 81) * 0.36), y = height - h;
      ctx.globalAlpha = 0.93; housing(ctx, x, y, w, h, c, 7);
      ctx.globalAlpha = 0.4; ctx.strokeStyle = c.wire;
      for (let n = 0; n < 7; n++) trace(ctx, [[x + 12, y + 24 + n * 13], [x + w - 12, y + 24 + n * 13]]);
      ctx.fillStyle = i % 3 ? c.cyan : c.violet; ctx.globalAlpha = 0.64;
      ctx.fillRect(x + 10, y + 11, Math.max(4, w - 20), 2);
      for (let n = 0; n < 3; n++) ctx.fillRect(x + 12 + n * 7, y + h - 15, 3, 3);
      ctx.globalAlpha = 0.22; ctx.strokeStyle = c.steel;
      trace(ctx, [[x + w + 2, y + 12], [x + w + 9, y + 19], [x + w + 9, height]]);
    }
  }
  ctx.globalAlpha = 1;
}

/** Moving data remains sparse and follows the same traces as the machinery.
 * @param {CanvasRenderingContext2D} ctx @param {number} index @param {number} width
 * @param {number} height @param {number} time @param {number} energy @param {Palette} c
 */
export function paintSignals(ctx, index, width, height, time, energy, c) {
  const size = Math.min(height * 0.67, 340), x = width * 0.48 - size / 2, y = height * 0.12;
  ctx.strokeStyle = c.cyan; ctx.lineWidth = 1;
  for (let i = 0; i < 7; i++) {
    const sy = y + 28 + i * (size - 56) / 9, bend = 36 + i * 12;
    const progress = (time * 0.16 + i * 0.147 + variation(index, 91)) % 1;
    const px = -70 + progress * (x + 70);
    ctx.globalAlpha = 0.16 + energy * 0.35;
    trace(ctx, [[0, sy + bend], [x - bend, sy + bend], [x - 20, sy], [x, sy]]);
    const travelY = px < x - bend ? sy + bend : px < x - 20 ? sy + bend * (x - 20 - px) / (bend - 20) : sy;
    ctx.globalAlpha = 0.5 + energy * 0.35; ctx.fillStyle = c.cyan;
    ctx.fillRect(px, travelY - 1, 6, 2);
  }
  ctx.globalAlpha = 0.25 + energy * 0.5;
  ctx.strokeStyle = c.cyan; ctx.lineWidth = 2;
  ctx.strokeRect(x + 22, y + 22, size - 44, size - 44);
  if (energy > 0.01) {
    const glow = ctx.createRadialGradient(x + size / 2, y + size / 2, size * 0.3, x + size / 2, y + size / 2, size);
    glow.addColorStop(0, c.cyan); glow.addColorStop(1, 'transparent');
    ctx.globalAlpha = energy * 0.09; ctx.fillStyle = glow;
    ctx.fillRect(x - size / 2, y - size / 2, size * 2, size * 2);
  }
  ctx.globalAlpha = 1;
}

/** Perspective circuit floor. Gameplay terrain cuts its actual gaps above this.
 * @param {CanvasRenderingContext2D} ctx @param {number} width @param {number} height
 * @param {number} floor @param {number} camera @param {Palette} c
 */
export function paintGrid(ctx, width, height, floor, camera, c) {
  const horizon = floor - Math.min(100, height * 0.16), span = floor - horizon;
  const wash = ctx.createLinearGradient(0, horizon, 0, height);
  wash.addColorStop(0, 'transparent'); wash.addColorStop(Math.min(0.95, span / Math.max(1, height - horizon)), c.floor); wash.addColorStop(1, c.void);
  ctx.fillStyle = wash; ctx.fillRect(0, horizon, width, height - horizon);
  ctx.strokeStyle = c.wire; ctx.lineWidth = 1; ctx.globalAlpha = 0.45;
  for (let i = -1; i < width / 80 + 2; i++) {
    const x = i * 80 - ((camera % 80) + 80) % 80;
    trace(ctx, [[width / 2 + (x - width / 2) * 0.1, horizon], [x, floor]]);
  }
  for (let row = 1; row < 9; row++) {
    const y = horizon + (row / 8) ** 2 * span;
    trace(ctx, [[0, y], [width, y]]);
  }
  ctx.globalAlpha = 1;
}
